import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { collabAPI, serviceDeliveryAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Clapperboard, Plus, Mail, Trash2, Pencil, Copy, Loader2, Shield } from 'lucide-react';

const ClientChips = ({ clients, selected, onToggle }) => (
  <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto" data-testid="collab-client-chips">
    {clients.map((c) => {
      const id = c._id || c.id; const on = selected.includes(id);
      return (
        <button key={id} type="button" onClick={() => onToggle(id)} data-testid={`collab-client-chip-${id}`}
          className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${on ? 'bg-primary text-primary-foreground border-primary' : 'bg-background hover:bg-muted'}`}>
          {c.nombre || c.name}
        </button>
      );
    })}
  </div>
);

const empty = { id: null, name: '', email: '', empresa: '', client_ids: [] };

export const CollaboratorsAdmin = () => {
  const [list, setList] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const load = async () => {
    try { setList(await collabAPI.adminList()); } catch (e) { toast.error(e?.message || 'Error cargando colaboradores'); }
    setLoading(false);
  };
  useEffect(() => { load(); serviceDeliveryAPI.getClients().then(setClients).catch(() => {}); }, []);

  const toggle = (id) => setForm({ ...form, client_ids: form.client_ids.includes(id) ? form.client_ids.filter((x) => x !== id) : [...form.client_ids, id] });
  const save = async () => {
    setSaving(true);
    try {
      if (form.id) {
        await collabAPI.adminUpdate(form.id, { name: form.name, empresa: form.empresa, client_ids: form.client_ids });
        toast.success('Colaborador actualizado');
      } else {
        const r = await collabAPI.adminInvite({ name: form.name, email: form.email, empresa: form.empresa || null, client_ids: form.client_ids });
        setResult(r);
        toast.success(r.email_sent ? `Invitación enviada a ${form.email}` : 'Colaborador creado (copia el enlace de alta)');
      }
      setForm(null); await load();
    } catch (e) { toast.error(e?.message || 'No se pudo guardar'); }
    setSaving(false);
  };
  const remove = async (u) => {
    if (!window.confirm(`¿Eliminar el acceso de ${u.name}? Dejará de poder entrar al portal.`)) return;
    try { await collabAPI.adminDelete(u.id); toast.success('Acceso eliminado'); await load(); } catch (e) { toast.error(e?.message); }
  };
  const resend = async (u) => {
    try { const r = await collabAPI.adminResend(u.id); if (r.email_sent) toast.success('Invitación reenviada'); else setResult({ ...u, ...r }); }
    catch (e) { toast.error(e?.message); }
  };
  const toggleActive = async (u) => {
    try { await collabAPI.adminUpdate(u.id, { is_active: u.is_active === false }); await load(); } catch (e) { toast.error(e?.message); }
  };

  return (
    <div className="space-y-4" data-testid="collaborators-admin">
      <Card className="border-slate-200 bg-slate-50/50">
        <CardContent className="pt-4 text-sm text-slate-700 flex gap-2"><Shield className="w-4 h-4 mt-0.5 shrink-0" />
          <p>Los colaboradores externos (p. ej. filmmakers) solo ven, de los clientes que les asignes, las publicaciones pendientes de grabar con su guion y portada, las fechas de grabación y el material de marca. Pueden marcar como «Grabado» lo que aún no esté grabado. No ven copies, estrategia, otros clientes, finanzas, equipo ni tareas; cualquier otra ruta del portal les devuelve acceso denegado.</p>
        </CardContent>
      </Card>
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold flex items-center gap-2"><Clapperboard className="w-5 h-5 text-primary" /> Colaboradores externos <Badge variant="outline">{list.length}</Badge></h3>
        <Button onClick={() => setForm({ ...empty })} data-testid="invite-collab-btn"><Plus className="w-4 h-4 mr-2" /> Invitar colaborador</Button>
      </div>
      {loading ? <Loader2 className="w-5 h-5 animate-spin text-primary" /> : list.length === 0 ? (
        <p className="text-sm text-muted-foreground py-8 text-center">Aún no hay colaboradores externos.</p>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {list.map((u) => (
            <Card key={u.id} className={u.is_active === false ? 'opacity-60' : ''} data-testid={`collab-card-${u.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <CardTitle className="text-base">{u.name}{u.empresa ? <span className="text-muted-foreground font-normal"> · {u.empresa}</span> : null}</CardTitle>
                    <CardDescription>{u.email}</CardDescription>
                  </div>
                  <div className="flex gap-1">
                    {u.pending_setup && <Badge variant="outline" className="text-amber-700 border-amber-300">Pendiente de alta</Badge>}
                    {u.is_active === false && <Badge variant="outline" className="text-red-700 border-red-300">Desactivado</Badge>}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex flex-wrap gap-1">
                  {u.clients.length === 0 ? <span className="text-xs text-muted-foreground">Sin clientes asignados</span>
                    : u.clients.map((c) => <Badge key={c.id} className="bg-purple-100 text-purple-800">{c.nombre}</Badge>)}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <Button size="sm" variant="outline" onClick={() => setForm({ id: u.id, name: u.name, email: u.email, empresa: u.empresa || '', client_ids: u.clients.map((c) => c.id) })} data-testid={`edit-collab-${u.id}`}><Pencil className="w-3.5 h-3.5 mr-1" /> Clientes</Button>
                  {u.pending_setup && <Button size="sm" variant="outline" onClick={() => resend(u)} data-testid={`resend-collab-${u.id}`}><Mail className="w-3.5 h-3.5 mr-1" /> Reenviar invitación</Button>}
                  <Button size="sm" variant="outline" onClick={() => toggleActive(u)} data-testid={`toggle-collab-${u.id}`}>{u.is_active === false ? 'Reactivar' : 'Desactivar'}</Button>
                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(u)} data-testid={`delete-collab-${u.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-lg" data-testid="collab-form-dialog">
          <DialogHeader><DialogTitle>{form?.id ? 'Editar colaborador' : 'Invitar colaborador externo'}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Formulario de colaborador externo</DialogDescription>
          {form && (
            <div className="space-y-3 py-1">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Nombre</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ej: Lucía (filmmaker)" data-testid="collab-name-input" /></div>
                <div><Label>Empresa / agencia</Label><Input value={form.empresa} onChange={(e) => setForm({ ...form, empresa: e.target.value })} placeholder="Ej: Studio Málaga Films" data-testid="collab-empresa-input" /></div>
              </div>
              <div><Label>Email</Label><Input type="email" value={form.email} disabled={!!form.id} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="nombre@agencia.com" data-testid="collab-email-input" /></div>
              <div>
                <Label>Clientes a los que tendrá acceso</Label>
                <ClientChips clients={clients} selected={form.client_ids} onToggle={toggle} />
                <p className="text-[11px] text-muted-foreground mt-1">Solo verá el contenido a grabar y el material de marca de estos clientes.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)}>Cancelar</Button>
            <Button onClick={save} disabled={saving || !form?.name.trim() || (!form?.id && !form?.email.trim()) || !form?.client_ids.length} data-testid="save-collab-btn">
              {saving ? 'Guardando…' : (form?.id ? 'Guardar' : 'Crear e invitar')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!result} onOpenChange={(o) => !o && setResult(null)}>
        <DialogContent className="max-w-lg" data-testid="collab-invite-result">
          <DialogHeader><DialogTitle>{result?.email_sent ? 'Invitación enviada' : 'Colaborador creado'}</DialogTitle></DialogHeader>
          <DialogDescription>{result?.email_sent ? `Hemos enviado a ${result.email} un email para crear su contraseña (válido 48 h).` : 'No se pudo enviar el email automáticamente. Comparte este enlace de alta (válido 48 h):'}</DialogDescription>
          {result?.setup_link && (
            <div className="flex gap-2">
              <Input readOnly value={result.setup_link} data-testid="collab-setup-link" />
              <Button variant="outline" onClick={() => { navigator.clipboard.writeText(result.setup_link); toast.success('Enlace copiado'); }} data-testid="copy-collab-link-btn"><Copy className="w-4 h-4" /></Button>
            </div>
          )}
          <DialogFooter><Button onClick={() => setResult(null)}>Cerrar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
